import React, { useState } from 'react';
import {
  FaTimes,
  FaUserMinus,
  FaInfoCircle,
  FaCheck,
  FaTextHeight,
  FaList,
  FaCalendarAlt,
  FaBell,
  FaBellSlash,
  FaStickyNote,
  FaExclamationTriangle
} from 'react-icons/fa';
import styles from './MemberRemovalModal.module.css';

const REMOVAL_TYPES = [
  { value: 'violation', label: 'Policy Violation' },
  { value: 'inactivity', label: 'Inactivity' },
  { value: 'request', label: 'Member Request' },
  { value: 'restructuring', label: 'Club Restructuring' },
  { value: 'non_payment', label: 'Non-Payment' },
  { value: 'other', label: 'Other' }
];

const MemberRemovalModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  memberName, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState({
    reason: '',
    removalType: '',
    effectiveDate: '',
    notifyMember: true,
    additionalNotes: ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }

    // Validate effective date if provided
    if (formData.effectiveDate) {
      const selectedDate = new Date(formData.effectiveDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.effectiveDate = 'Effective date cannot be in the past';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const removalData = {
      reason: formData.reason.trim(),
      ...(formData.removalType && { removalType: formData.removalType }),
      ...(formData.effectiveDate && { effectiveDate: new Date(formData.effectiveDate).toISOString() }),
      notifyMember: formData.notifyMember,
      ...(formData.additionalNotes.trim() && { additionalNotes: formData.additionalNotes.trim() })
    };

    onConfirm(removalData);
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        reason: '',
        removalType: '',
        effectiveDate: '',
        notifyMember: true,
        additionalNotes: ''
      });
      setErrors({});
      onClose();
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const isImmediateRemoval = !formData.effectiveDate || formData.effectiveDate === getTodayDate();

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <FaUserMinus className={styles.removalIcon} />
          </div>
          <div className={styles.headerText}>
            <h2>Remove Club Member</h2>
            <p>Provide details for removing {memberName} from the club</p>
          </div>
          <button 
            className={styles.closeButton} 
            onClick={handleClose}
            disabled={isLoading}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          {/* Reason Field - Required */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaTextHeight className={styles.labelIcon} />
              Reason for Removal *
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder="Please provide a clear reason for removing this member..."
              className={`${styles.textarea} ${errors.reason ? styles.error : ''}`}
              rows={4}
              disabled={isLoading}
            />
            {errors.reason && (
              <span className={styles.errorMessage}>{errors.reason}</span>
            )}
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              This will be included in the notification if member notification is enabled
            </div>
          </div>

          {/* Removal Type Field - Optional */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaList className={styles.labelIcon} />
              Removal Type
            </label>
            <select
              value={formData.removalType}
              onChange={(e) => handleInputChange('removalType', e.target.value)}
              className={styles.select}
              disabled={isLoading}
            >
              <option value="">Select a type (optional)</option>
              {REMOVAL_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              Helps categorize removals for analytics and reporting
            </div>
          </div>

          {/* Effective Date Field - Optional */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaCalendarAlt className={styles.labelIcon} />
              Effective Date
            </label>
            <input
              type="date"
              value={formData.effectiveDate}
              onChange={(e) => handleInputChange('effectiveDate', e.target.value)}
              min={getTodayDate()}
              className={`${styles.dateInput} ${errors.effectiveDate ? styles.error : ''}`}
              disabled={isLoading}
            />
            {errors.effectiveDate && (
              <span className={styles.errorMessage}>{errors.effectiveDate}</span>
            )}
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              {isImmediateRemoval 
                ? "Member will be removed immediately"
                : "Member will be removed on the selected date"
              }
            </div>
          </div>

          {/* Notify Member Checkbox */}
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.notifyMember}
                onChange={(e) => handleInputChange('notifyMember', e.target.checked)}
                className={styles.checkbox}
                disabled={isLoading}
              />
              <span className={styles.checkboxCustom}>
                {formData.notifyMember && <FaCheck className={styles.checkIcon} />}
              </span>
              <div className={styles.checkboxText}>
                {formData.notifyMember ? (
                  <FaBell className={styles.labelIcon} />
                ) : (
                  <FaBellSlash className={styles.labelIcon} />
                )}
                Send notification to member
              </div>
            </label>
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              {formData.notifyMember 
                ? "The member will receive an email notification about their removal"
                : "The member will not be notified about their removal"
              }
            </div>
          </div>

          {/* Additional Notes Field - Optional */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FaStickyNote className={styles.labelIcon} />
              Additional Notes
            </label>
            <textarea
              value={formData.additionalNotes}
              onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
              placeholder="Any additional information or context for this removal..."
              className={styles.textarea}
              rows={3}
              disabled={isLoading}
            />
            <div className={styles.fieldHint}>
              <FaInfoCircle className={styles.hintIcon} />
              Internal notes for record keeping (not sent to the member)
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.removeButton}
              disabled={isLoading || !formData.reason.trim()}
            >
              {isLoading ? (
                <>
                  <div className={styles.spinner}></div>
                  Removing...
                </>
              ) : (
                <>
                  <FaUserMinus />
                  {isImmediateRemoval ? 'Remove Member' : 'Schedule Removal'}
                </>
              )}
            </button>
          </div>
        </form>

        {/* Warning Notice */}
        <div className={styles.warningNotice}>
          <FaExclamationTriangle className={styles.noticeIcon} />
          <div className={styles.noticeText}>
            <strong>Important:</strong> This action will permanently remove the member from the club. 
            {formData.notifyMember && ' The member will receive an email notification.'} 
            {!isImmediateRemoval && ' The removal will be processed on the selected date.'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberRemovalModal;

